import { useEffect, useRef, useState } from 'react';
import { Link, NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Bell,
  BookOpen,
  ChevronDown,
  Download,
  Globe,
  Grid3x3,
  Home,
  LogOut,
  Menu,
  Moon,
  Radio,
  Search,
  Settings,
  ShieldAlert,
  Sun,
  Swords,
  Puzzle,
  Trophy,
  User as UserIcon,
  Users,
  Wrench,
  X,
} from 'lucide-react';

import { api } from '@/lib/api';
import { useSession } from '@/store/session';
import { useFocusMode } from '@/store/focus';
import { useSocketStatus } from '@/lib/useSocket';
import { SearchPalette } from '@/components/SearchPalette';
import { NotificationsMenu } from '@/components/NotificationsMenu';
import { MessagesButton } from '@/components/MessagesButton';
import { ChallengeToasts } from '@/features/challenges/ChallengeToasts';
import { OngoingGamesBanner } from '@/features/game/OngoingGamesBanner';
import { useInstallPrompt } from '@/lib/pwa';
import { usePushSync } from '@/lib/push';
import { setThemeChoice, useTheme } from '@/lib/theme';
import { Avatar } from '@/components/Avatar';
import { LANGUAGES, setLanguage, t, useLanguage, type Language } from '@/lib/i18n';

/**
 * Menú principal, con un desplegable por sección.
 *
 * Cada entrada lleva a una página concreta; las que todavía no existen
 * apuntan a su ficha de «en construcción», que cuenta qué llevará, en vez
 * de a un enlace roto.
 */
interface NavItem {
  to: string;
  label: string;
  /** Solo lo ven las cuentas VIP. */
  vip?: boolean;
}

interface NavGroup {
  label: string;
  icon: typeof Swords;
  items: NavItem[];
}

const NAV: NavGroup[] = [
  {
    label: 'Jugar',
    icon: Swords,
    items: [
      { to: '/jugar', label: 'Crear partida en la sala de espera' },
      { to: '/desafios', label: 'Desafía a un amigo' },
      { to: '/torneos', label: 'Torneos' },
      { to: '/torneos?tipo=suizo', label: 'Torneos suizos' },
      { to: '/simultaneas', label: 'Partidas simultáneas' },
      { to: '/tablero', label: 'Tablero local' },
    ],
  },
  {
    label: 'Ejercicios',
    icon: Puzzle,
    items: [
      { to: '/tactica', label: 'Ejercicios' },
      { to: '/tactica?modo=temas', label: 'Ejercicios por temas' },
      { to: '/tactica?modo=panel', label: 'Panel de ejercicios' },
      { to: '/tactica?modo=racha', label: 'Racha' },
      { to: '/tactica?modo=tormenta', label: 'Tormenta' },
      { to: '/carrera', label: 'Carrera' },
      { to: '/tactica?modo=maraton', label: 'Maratón' },
      { to: '/tactica?modo=mias', label: 'De mis partidas' },
    ],
  },
  {
    label: 'Aprender',
    icon: BookOpen,
    items: [
      { to: '/aprender/de-zero-a-hero', label: 'De Zero a Hero' },
      { to: '/aprender/pro-tactics', label: 'Pro-Tactics', vip: true },
      { to: '/aprender/pro-endgames', label: 'Pro-Endgames', vip: true },
      { to: '/practicar', label: 'Practicar' },
      { to: '/coordenadas', label: 'Coordenadas' },
      { to: '/estudios', label: 'Estudiar' },
      { to: '/entrenadores', label: 'Entrenadores' },
    ],
  },
  {
    label: 'Ver',
    icon: Radio,
    items: [
      { to: '/tv', label: 'UAChess TV' },
      { to: '/tv?vista=cuadricula', label: 'Partidas actuales' },
      { to: '/retransmisiones', label: 'Retransmisiones' },
      { to: '/videos', label: 'Biblioteca de vídeos' },
    ],
  },
  {
    label: 'Comunidad',
    icon: Users,
    items: [
      { to: '/comunidades', label: 'Comunidades' },
      { to: '/clasificacion', label: 'Clasificación' },
      { to: '/foro', label: 'Foro' },
      { to: '/blog', label: 'Blog' },
    ],
  },
  {
    label: 'Herramientas',
    icon: Wrench,
    items: [
      { to: '/analisis', label: 'Tablero de análisis' },
      { to: '/aperturas', label: 'Aperturas' },
      { to: '/editor', label: 'Editor de tablero' },
      { to: '/pareo', label: 'Pareo de torneos' },
      { to: '/reloj', label: 'Reloj de ajedrez' },
      { to: '/analisis?importar=1', label: 'Importar partida' },
      { to: '/busqueda', label: 'Búsqueda avanzada' },
    ],
  },
];


export function Layout() {
  const user = useSession((s) => s.user);
  usePushSync(user?.id ?? null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const location = useLocation();
  const focused = useFocusMode((s) => s.playing);
  const isBoardApp =
    location.pathname.startsWith('/partida') ||
    location.pathname.startsWith('/tactica') ||
    location.pathname.startsWith('/puzzles') ||
    location.pathname.startsWith('/analisis');

  // Al cambiar de página se cierra el menú móvil.
  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  // Ctrl/Cmd+K abre la búsqueda desde cualquier parte.
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        setSearchOpen(true);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  return (
    <div className="relative min-h-dvh flex flex-col bg-bg selection:bg-brand/30 selection:text-white">
      {/* Resplandor ambiental de cabecera */}
      <div className="pointer-events-none fixed inset-0 z-0 bg-[radial-gradient(ellipse_80%_45%_at_50%_-15%,rgba(37,99,235,0.14),rgba(255,255,255,0))]" />

      <header className="sticky top-0 z-40 border-b border-white/[0.12] bg-[#090a0f]/95 pt-[env(safe-area-inset-top)] shadow-[0_4px_30px_rgba(0,0,0,0.6)] backdrop-blur-xl">
        <div className="mx-auto flex h-14 max-w-[1400px] items-center gap-2 px-4 sm:px-6 lg:px-8">
          <button
            type="button"
            className="btn btn-quiet -ml-1 p-2 text-white hover:bg-white/10 lg:hidden"
            onClick={() => setMenuOpen((v) => !v)}
            aria-label={menuOpen ? t('Cerrar menú') : t('Abrir menú')}
            aria-expanded={menuOpen}
          >
            {menuOpen ? <X size={20} /> : <Menu size={20} />}
          </button>

          <Link
            to="/"
            className="group flex shrink-0 items-center mr-4 sm:mr-6 lg:mr-8 transition-transform duration-200 hover:opacity-95"
            aria-label="UAChess"
          >
            <img
              src="/brand/logo-nav.png"
              srcSet="/brand/logo-nav-72.png 1x, /brand/logo-nav.png 2x"
              alt="UAChess"
              className="h-8 sm:h-[34px] w-auto object-contain transition-transform duration-200 group-hover:scale-105 drop-shadow-[0_2px_10px_rgba(59,130,246,0.25)]"
            />
          </Link>

          <nav className="hidden items-center gap-1 lg:flex">
            {NAV.map((group) => (
              <NavMenu key={group.label} group={group} />
            ))}
          </nav>

          <div className="flex-1" />

          <button
            type="button"
            onClick={() => setSearchOpen(true)}
            className="btn btn-icon border-white/15 bg-white/[0.08] text-white hover:bg-white/15 hover:text-white transition-all hover:scale-105 active:scale-95"
            aria-label={t('Buscar')}
            title={`${t('Buscar')} (Ctrl K)`}
          >
            <Search size={18} />
          </button>

          {/* Con sesión, en el móvil va dentro del menú de usuario: la cabecera no da para más. */}
          <ThemeButton className={user ? 'max-sm:hidden' : ''} />
          {user ? <MessagesButton /> : null}
          {user ? <NotificationsMenu /> : null}

          {user ? <UserMenu /> : <AuthButtons />}
        </div>

        {menuOpen ? <MobileNav /> : null}
      </header>

      <ConnectionBanner />
      {user ? <OngoingGamesBanner /> : null}

      <main
        className={`mx-auto w-full max-w-[1400px] px-2.5 sm:px-4 ${
          isBoardApp
            ? 'flex-1 min-h-0 flex flex-col pt-1.5 sm:pt-2 pb-1.5'
            : `pt-2.5 sm:pt-3 lg:pb-6 ${
                focused
                  ? 'pb-[calc(env(safe-area-inset-bottom)+1.5rem)]'
                  : 'pb-[calc(var(--nav-h)+env(safe-area-inset-bottom)+1.5rem)]'
              }`
        }`}
      >
        <Outlet />
        {isBoardApp ? null : (
          <footer className="mt-4 sm:mt-6 flex flex-wrap items-center justify-center gap-x-5 gap-y-1 border-t border-border pt-2 text-xs text-faint">
            <Link to="/terminos" className="py-2 hover:text-text">{t('Términos de uso')}</Link>
            <Link to="/ayuda" className="py-2 hover:text-text">{t('Preguntas frecuentes')}</Link>
            <LanguageSelect />
          </footer>
        )}
      </main>

      {focused ? null : <MobileTabBar />}

      {searchOpen ? <SearchPalette onClose={() => setSearchOpen(false)} /> : null}

      {/* Retos entrantes: se aceptan sin salir de donde estes. */}
      {user ? <ChallengeToasts /> : null}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Sesión                                                              */
/* ------------------------------------------------------------------ */

function AuthButtons() {
  return (
    <div className="flex items-center gap-1 sm:gap-2">
      <Link to="/entrar" className="btn px-2.5 text-sm font-semibold text-white/90 hover:bg-white/10 hover:text-white sm:px-4">
        {t('Entrar')}
      </Link>
      <Link to="/registro" className="btn btn-primary px-3 text-sm font-semibold text-white sm:px-4">
        {t('Crear cuenta')}
      </Link>
    </div>
  );
}

/** Cambia entre día y noche; «Sistema» se elige en Ajustes. */
function ThemeButton({ className = '' }: { className?: string }) {
  const { theme } = useTheme();
  const next = theme === 'dark' ? 'light' : 'dark';
  return (
    <button
      type="button"
      className={`btn btn-icon border-white/15 bg-white/[0.08] text-white hover:bg-white/15 hover:text-white ${className}`}
      onClick={() => setThemeChoice(next)}
      aria-label={next === 'light' ? t('Cambiar a modo día') : t('Cambiar a modo noche')}
      title={next === 'light' ? t('Modo día') : t('Modo noche')}
    >
      {theme === 'dark' ? <Sun size={18} /> : <Moon size={18} />}
    </button>
  );
}

function UserMenu() {
  const user = useSession((s) => s.user)!;
  const logout = useSession((s) => s.logout);
  const [open, setOpen] = useState(false);
  const { install } = useInstallPrompt();
  const ref = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (!open) return;
    const onClick = (event: MouseEvent) => {
      if (!ref.current?.contains(event.target as Node)) setOpen(false);
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        className="btn gap-1.5 px-2 text-white/90 hover:bg-white/10 hover:text-white"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
      >
        <span className="text-lg leading-none" aria-hidden="true"><Avatar value={user.avatar} /></span>
        <span className="hidden max-w-28 truncate-1 text-sm font-semibold text-white sm:block">{user.username}</span>
        <ChevronDown size={14} className="hidden text-white/80 sm:block" />
      </button>

      {open ? (
        <div
          role="menu"
          className="animate-pop-in absolute right-0 mt-1 w-56 overflow-hidden popover"
        >
          <div className="border-b border-border px-3 py-2.5">
            <div className="truncate-1 font-medium">{user.username}</div>
            <div className="truncate-1 text-xs text-faint">{user.email}</div>
          </div>

          <MenuLink to={`/@/${user.username}`} icon={UserIcon} onSelect={() => setOpen(false)}>
            {t('Mi perfil')}
          </MenuLink>
          <MenuLink to="/ajustes" icon={Settings} onSelect={() => setOpen(false)}>
            {t('Ajustes')}
          </MenuLink>
          <ThemeMenuItem onSelect={() => setOpen(false)} />
          {install ? (
            <button
              type="button"
              role="menuitem"
              className="flex w-full items-center gap-2.5 px-3 py-2.5 text-left text-sm text-muted transition-colors hover:bg-hover hover:text-text"
              onClick={() => {
                setOpen(false);
                void install();
              }}
            >
              <Download size={16} />
              {t('Instalar la aplicación')}
            </button>
          ) : null}
          {user.role !== 'user' ? (
            <MenuLink to="/moderacion" icon={ShieldAlert} onSelect={() => setOpen(false)}>
              {t('Moderación')}
            </MenuLink>
          ) : null}

          <div className="divider my-1" />

          <button
            type="button"
            role="menuitem"
            className="flex w-full items-center gap-2.5 px-3 py-2.5 text-left text-sm text-muted transition-colors hover:bg-hover hover:text-text"
            onClick={async () => {
              setOpen(false);
              await logout();
              navigate('/');
            }}
          >
            <LogOut size={16} />
            {t('Cerrar sesión')}
          </button>
        </div>
      ) : null}
    </div>
  );
}

/** Día / noche dentro del menú de usuario; solo en el móvil. */
function ThemeMenuItem({ onSelect }: { onSelect: () => void }) {
  const { theme } = useTheme();
  const next = theme === 'dark' ? 'light' : 'dark';
  return (
    <button
      type="button"
      role="menuitem"
      className="flex w-full items-center gap-2.5 px-3 py-2.5 text-left text-sm text-muted transition-colors hover:bg-hover hover:text-text sm:hidden"
      onClick={() => {
        onSelect();
        setThemeChoice(next);
      }}
    >
      {theme === 'dark' ? <Sun size={16} /> : <Moon size={16} />}
      {next === 'light' ? t('Modo día') : t('Modo noche')}
    </button>
  );
}

function MenuLink({
  to,
  icon: Icon,
  children,
  onSelect,
}: {
  to: string;
  icon: typeof UserIcon;
  children: React.ReactNode;
  onSelect: () => void;
}) {
  return (
    <Link
      to={to}
      role="menuitem"
      onClick={onSelect}
      className="flex items-center gap-2.5 px-3 py-2.5 text-sm text-muted transition-colors hover:bg-hover hover:text-text"
    >
      <Icon size={16} />
      {children}
    </Link>
  );
}

/* ------------------------------------------------------------------ */
/* Navegación móvil                                                    */
/* ------------------------------------------------------------------ */

/** Entradas del grupo que puede ver la cuenta actual. */
function useVisibleItems(items: NavItem[]): NavItem[] {
  const isVip = useSession((s) => s.user?.isVip ?? false);
  return items.filter((item) => !item.vip || isVip);
}

function MobileNav() {
  return (
    <nav className="animate-fade-in max-h-[calc(100dvh-var(--nav-h)-4.25rem)] overflow-y-auto overscroll-contain border-t border-white/10 bg-[#0c0f18]/98 p-3 shadow-[0_20px_50px_rgba(0,0,0,0.85)] backdrop-blur-2xl lg:hidden divide-y divide-white/[0.06]">
      {NAV.map(({ label, icon: Icon, items }) => (
        <div key={label} className="py-2.5 first:pt-1 last:pb-1">
          <div className="mb-1.5 flex items-center gap-2 px-3 text-xs font-bold text-blue-400">
            <Icon size={14} className="text-blue-400" />
            <span>{t(label)}</span>
          </div>
          <div className="space-y-0.5">
            <MobileNavItems items={items} />
          </div>
        </div>
      ))}
    </nav>
  );
}

function MobileNavItems({ items }: { items: NavItem[] }) {
  return (
    <>
      {useVisibleItems(items).map((item) => (
        <NavLink
          key={item.to}
          to={item.to}
          className={({ isActive }) =>
            `flex items-center justify-between rounded-xl px-3.5 py-2.5 text-sm font-medium transition-all ${
              isActive
                ? 'border border-blue-500/40 bg-blue-600/20 font-semibold text-white shadow-[0_2px_10px_rgba(37,99,235,0.25)]'
                : 'text-white/85 hover:bg-white/10 hover:text-white active:bg-white/15'
            }`
          }
        >
          <span>{t(item.label)}</span>
          {item.vip ? (
            <span className="title-badge text-[10px]">VIP</span>
          ) : null}
        </NavLink>
      ))}
    </>
  );
}

/**
 * Un grupo del menú de escritorio: se abre al pasar el ratón y también al
 * pulsar, que es lo que necesita quien navega con el teclado. Se cierra solo
 * al cambiar de página o con Escape.
 */
function NavMenu({ group }: { group: NavGroup }) {
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const Icon = group.icon;
  const items = useVisibleItems(group.items);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname, location.search]);

  useEffect(() => {
    if (!open) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open]);

  const active = group.items.some((item) => location.pathname === item.to.split('?')[0]);

  const hoverable =
    typeof window !== 'undefined' && window.matchMedia('(hover: hover)').matches;

  const handleMouseEnter = () => {
    setOpen(true);
  };

  const handleMouseLeave = () => {
    setOpen(false);
  };

  return (
    <div
      className="relative"
      onMouseEnter={hoverable ? handleMouseEnter : undefined}
      onMouseLeave={hoverable ? handleMouseLeave : undefined}
    >
      {/* Con ratón, el nombre lleva a la sección principal del grupo (la
          primera del menú) y el menú sale al pasar por encima. */}
      {hoverable ? (
        <Link
          to={group.items[0].to}
          onClick={() => setOpen(false)}
          onKeyDown={(event) => {
            // Con teclado: flecha abajo abre el menú sin navegar.
            if (event.key === 'ArrowDown') {
              event.preventDefault();
              setOpen(true);
            }
          }}
          aria-expanded={open}
          aria-haspopup="menu"
          className={`flex items-center gap-2 rounded-lg px-2.5 py-1.5 text-sm font-semibold transition-all xl:px-3 ${
            active
              ? 'bg-gradient-to-r from-blue-600 to-indigo-600 font-bold text-white shadow-[0_2px_12px_rgba(37,99,235,0.4)]'
              : open
                ? 'bg-white/15 text-white'
                : 'text-white hover:bg-white/10 hover:text-white'
          }`}
        >
          <Icon size={16} className="hidden text-white xl:block" />
          {t(group.label)}
        </Link>
      ) : (
        <button
          type="button"
          onClick={() => setOpen((value) => !value)}
          aria-expanded={open}
          aria-haspopup="menu"
          className={`flex items-center gap-2 rounded-lg px-2.5 py-1.5 text-sm font-semibold transition-all xl:px-3 ${
            active
              ? 'bg-gradient-to-r from-blue-600 to-indigo-600 font-bold text-white shadow-[0_2px_12px_rgba(37,99,235,0.4)]'
              : open
                ? 'bg-white/15 text-white'
                : 'text-white hover:bg-white/10 hover:text-white'
          }`}
        >
          <Icon size={16} className="hidden text-white xl:block" />
          {t(group.label)}
        </button>
      )}

      {open ? (
        <div
          role="menu"
          className="absolute left-0 top-full z-50 pt-1 before:absolute before:-top-2 before:inset-x-0 before:h-2 before:content-['']"
        >
          <div className="animate-pop-in w-64 overflow-hidden rounded-xl border border-white/20 bg-[#12151f]/98 p-1.5 shadow-[0_16px_48px_rgba(0,0,0,0.85)] backdrop-blur-2xl">
            {items.map((item) => (
              <Link
                key={item.to}
                to={item.to}
                role="menuitem"
                onClick={() => setOpen(false)}
                className="block rounded-lg px-3 py-2 text-sm font-medium text-white/90 transition-colors hover:bg-white/10 hover:text-white"
              >
                {t(item.label)}
                {item.vip ? (
                  <span className="title-badge ml-2 text-[10px]">VIP</span>
                ) : null}
              </Link>
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
}

/** Barra inferior fija en móvil con los destinos más usados; el resto, en el menú. */
function MobileTabBar() {
  const tabs = [
    { to: '/', label: 'Inicio', icon: Home, end: true },
    { to: '/tactica', label: 'Táctica', icon: Puzzle },
    { to: '/jugar', label: 'Jugar', icon: Swords },
    { to: '/estudios', label: 'Estudios', icon: BookOpen },
    { to: '/tv', label: 'TV', icon: Radio },
  ];

  return (
    <nav
      className="fixed inset-x-0 bottom-0 z-40 flex h-[calc(var(--nav-h)+env(safe-area-inset-bottom))] items-stretch border-t border-white/10 bg-[#0c0f18]/95 shadow-[0_-8px_30px_rgba(0,0,0,0.6)] backdrop-blur-xl lg:hidden"
      style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
    >
      {tabs.map(({ to, label, icon: Icon, end }) => (
        <NavLink
          key={to}
          to={to}
          end={end}
          className={({ isActive }) =>
            `relative flex flex-1 flex-col items-center justify-center gap-0.5 text-[11px] font-medium transition-all active:bg-white/5 ${
              isActive ? 'font-bold text-blue-400 drop-shadow-[0_0_10px_rgba(59,130,246,0.6)]' : 'text-slate-400 hover:text-slate-200'
            }`
          }
        >
          {({ isActive }) => (
            <>
              {/* Resplandor sapphire arriba en la pestaña activa */}
              {isActive ? (
                <span
                  className="absolute inset-x-3.5 top-0 h-[2.5px] rounded-b-full bg-gradient-to-r from-blue-500 to-indigo-500 shadow-[0_0_10px_rgba(59,130,246,0.9)]"
                  aria-hidden="true"
                />
              ) : null}
              <Icon size={20} strokeWidth={isActive ? 2.3 : 1.8} />
              <span>{t(label)}</span>
            </>
          )}
        </NavLink>
      ))}
    </nav>
  );
}

/** Selector de idioma del pie: está a la vista también sin cuenta. */
function LanguageSelect() {
  const language = useLanguage();
  return (
    <label className="flex items-center gap-1.5">
      <Globe size={14} aria-hidden="true" />
      <span className="sr-only">{t('Idioma')}</span>
      <select
        className="cursor-pointer rounded border border-border bg-card px-1.5 py-0.5 text-xs text-muted hover:text-text"
        value={language}
        onChange={(event) => setLanguage(event.target.value as Language)}
      >
        {LANGUAGES.map((option) => (
          <option key={option.id} value={option.id}>
            {option.name}
          </option>
        ))}
      </select>
    </label>
  );
}

/* ------------------------------------------------------------------ */
/* Aviso de conexión                                                   */
/* ------------------------------------------------------------------ */

function ConnectionBanner() {
  const status = useSocketStatus();
  const [visible, setVisible] = useState(false);

  // Solo se avisa si la caída dura más de un segundo: un parpadeo de red no
  // debe llenar la pantalla de avisos.
  useEffect(() => {
    if (status === 'open') {
      setVisible(false);
      return;
    }
    const timer = setTimeout(() => setVisible(true), 1000);
    return () => clearTimeout(timer);
  }, [status]);

  if (!visible) return null;

  return (
    <div className="flex items-center justify-center gap-2 bg-warn/15 px-4 py-1.5 text-center text-sm text-warn">
      <span className="animate-pulse-soft">●</span>
      {t('Reconectando con el servidor…')}
    </div>
  );
}
